import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, InputAdornment, Chip, Drawer, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import { buildEventsApiUrl } from '../../config/api';
import { isSiteAdminUser } from '../../config/admin';
import { getAuthUserId, getAuthUserDisplayName } from '../../utils/authUser';
import { buildAuthHeaders } from '../../utils/authFetch';

interface HeaderProps {
  onLocationClick: () => void;
  userLocation: { city: string; state: string } | null;
  searchRadius: number;
  onRadiusChange: (radius: number) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export function Header({ onLocationClick, userLocation, searchRadius, onRadiusChange, searchQuery, onSearchChange }: HeaderProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user, initialized } = useSelector((state: RootState) => state.auth);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [draftSearchQuery, setDraftSearchQuery] = useState(searchQuery);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userId = getAuthUserId(user as any);
  const isSiteAdmin = isSiteAdminUser(user as any);
  const hasStoredAuth = typeof window !== 'undefined' && Boolean(localStorage.getItem('crowdtrades_token')) && Boolean(localStorage.getItem('crowdtrades_user'));
  const showAuthenticatedNav = initialized && isAuthenticated && Boolean(userId) && hasStoredAuth;

  useEffect(() => {
    const refreshMailboxBadge = async () => {
      if (!isAuthenticated || !userId) {
        setUnreadMessages(0);
        return;
      }

      try {
        const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox?previewOnly=true&includeReplies=false`), {
          headers: buildAuthHeaders(userId)
        });
        if (!response.ok) return;
        const data = await response.json();
        setUnreadMessages(data.unreadCount || 0);
      } catch (error) {
        console.warn('Failed to load mailbox unread count', error);
      }
    };

    (window as any).__crowdTradesRefreshMailboxBadge = refreshMailboxBadge;
    return () => {
      delete (window as any).__crowdTradesRefreshMailboxBadge;
    };
  }, [isAuthenticated, userId]);

  useEffect(() => {
    let cancelled = false;

    const loadUnreadCount = async () => {
      if (!isAuthenticated || !userId) {
        if (!cancelled) setUnreadMessages(0);
        return;
      }

      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }

      try {
        const refresh = (window as any).__crowdTradesRefreshMailboxBadge;
        if (typeof refresh === 'function') {
          await refresh();
          return;
        }
      } catch (error) {
        console.warn('Failed to run shared mailbox badge refresh', error);
      }

      try {
        const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox?previewOnly=true&includeReplies=false`), {
          headers: buildAuthHeaders(userId)
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) {
          setUnreadMessages(data.unreadCount || 0);
        }
      } catch (error) {
        console.warn('Failed to load mailbox unread count', error);
      }
    };

    loadUnreadCount();
    const intervalId = window.setInterval(loadUnreadCount, 30000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadUnreadCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, userId]);

  useEffect(() => {
    setDraftSearchQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (draftSearchQuery !== searchQuery) {
        onSearchChange(draftSearchQuery);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [draftSearchQuery, searchQuery, onSearchChange]);

  const handleLogout = () => {
    dispatch(logout());
    setMobileMenuOpen(false);
    navigate('/');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const MobileNavButton = ({ label, onClick, variant = 'text' as const }: { label: string; onClick: () => void; variant?: 'text' | 'outlined' | 'contained' }) => (
    <Button
      fullWidth
      variant={variant}
      onClick={() => {
        closeMobileMenu();
        onClick();
      }}
      sx={{
        justifyContent: 'flex-start',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '14px',
        py: 1.25,
        px: 1.5
      }}
    >
      {label}
    </Button>
  );

  return (
    <Box sx={{ 
      backgroundColor: 'white', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      padding: '12px 20px',
      position: 'sticky',
      top: 0,
      zIndex: 100
    }}>
      <Box sx={{ 
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flexWrap: { xs: 'nowrap', md: 'wrap' }
      }}>
        {/* Left - Logo */}
        <Box
          onClick={() => navigate('/')}
          sx={{
            cursor: 'pointer',
            flexShrink: 0,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box
            component="img"
            src="/crowd-trades-logo.jpg"
            alt="Crowd Trades"
            sx={{
              height: { xs: 54, sm: 64 },
              width: 'auto',
              display: 'block',
              objectFit: 'contain'
            }}
          />
        </Box>

        {/* Center - Search Box */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: { xs: 'auto', md: 'auto' },
          flex: { xs: '1 1 auto', md: '0 0 auto' },
          minWidth: 0,
          order: { xs: 0, md: 0 }
        }}>
          <TextField
            placeholder="Search events..."
            value={draftSearchQuery}
            onChange={(e) => setDraftSearchQuery(e.target.value)}
            size="small"
            sx={{
              width: { xs: '100%', md: '300px' },
              minWidth: 0,
              flex: '1 1 auto',
              backgroundColor: '#f8f9fa',
              borderRadius: '20px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                '& fieldset': {
                  borderColor: '#e0e0e0'
                },
                '&:hover fieldset': {
                  borderColor: '#667eea'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea'
                }
              },
              '& .MuiInputBase-input': {
                padding: '8px 12px'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#6c757d', fontSize: '20px' }} />
                </InputAdornment>
              )
            }}
          />

          <IconButton
            aria-label="Change location"
            onClick={onLocationClick}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              border: '1px solid #e0e0e0',
              color: '#495057',
              flexShrink: 0
            }}
          >
            <LocationOnIcon />
          </IconButton>

          <IconButton
            aria-label="Open navigation menu"
            onClick={() => setMobileMenuOpen(true)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              border: '1px solid #e0e0e0',
              color: '#495057',
              flexShrink: 0
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Right - Location & Buttons */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' }, 
          alignItems: 'center', 
          gap: 1.5,
          flexShrink: 0
        }}>
          {/* Location Button */}
          <Button
            variant="outlined"
            startIcon={<LocationOnIcon />}
            onClick={onLocationClick}
            size="small"
            sx={{
              borderColor: '#e0e0e0',
              color: '#495057',
              backgroundColor: 'white',
              '&:hover': {
                borderColor: '#667eea',
                backgroundColor: '#f8f9fc'
              },
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              padding: '8px 16px',
              whiteSpace: 'nowrap'
            }}
          >
            {userLocation ? `${userLocation.city}, ${userLocation.state}` : 'Set Location'}
          </Button>

          {/* Dashboard Link */}
          {showAuthenticatedNav && (
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/dashboard')}
              sx={{
                color: '#495057',
                '&:hover': {
                  backgroundColor: '#f8f9fc',
                  color: '#667eea'
                },
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                padding: '8px 12px'
              }}
            >
              Dashboard
            </Button>
          )}

          {showAuthenticatedNav && (
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/mailbox')}
              sx={{ color: '#495057', '&:hover': { backgroundColor: '#f8f9fc', color: '#667eea' }, textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', padding: '8px 12px' }}
            >
              Mail{unreadMessages > 0 ? ` (${unreadMessages})` : ''}
            </Button>
          )}

          {showAuthenticatedNav && isSiteAdmin && (
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/site-admin')}
              href="#/site-admin"
              sx={{
                color: '#495057',
                '&:hover': {
                  backgroundColor: '#f8f9fc',
                  color: '#667eea'
                },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                padding: '8px 12px'
              }}
            >
              Site Admin
            </Button>
          )}

          {/* Auth Button */}
          {showAuthenticatedNav ? (
            <>
              <Box
                onClick={() => navigate(`/users/${userId}`)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '999px',
                  '&:hover': {
                    backgroundColor: '#f8f9fc'
                  }
                }}
              >
                <Box sx={{
                  fontSize: '0.875rem',
                  color: '#667eea',
                  fontWeight: 500
                }}>
                  {getAuthUserDisplayName(user as any) || 'User'}
                </Box>
                {unreadMessages > 0 && (
                  <Chip
                    label="Unread mail"
                    size="small"
                    color="warning"
                    sx={{
                      height: '22px',
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleLogout}
                sx={{
                  borderColor: '#e0e0e0',
                  color: '#495057',
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: '#f8f9fc'
                  },
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  padding: '8px 16px'
                }}
              >
                Logout
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/login')}
              sx={{
                backgroundColor: '#667eea',
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                padding: '8px 16px',
                '&:hover': {
                  backgroundColor: '#764ba2'
                }
              }}
            >
              Login / Sign Up
            </Button>
          )}
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        PaperProps={{
          sx: {
            width: 'min(88vw, 360px)',
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Menu</Box>
          <IconButton aria-label="Close navigation menu" onClick={closeMobileMenu}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Button
          variant="outlined"
          startIcon={<LocationOnIcon />}
          onClick={() => {
            closeMobileMenu();
            onLocationClick();
          }}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            borderRadius: '14px',
            py: 1.25,
            px: 1.5
          }}
        >
          {userLocation ? `${userLocation.city}, ${userLocation.state}` : 'Set Location'}
        </Button>

        <MobileNavButton label="Home" onClick={() => navigate('/')} />
        {showAuthenticatedNav && <MobileNavButton label="Dashboard" onClick={() => navigate('/dashboard')} />}
        {showAuthenticatedNav && <MobileNavButton label={`Mailbox${unreadMessages > 0 ? ` (${unreadMessages})` : ''}`} onClick={() => navigate('/mailbox')} />}
        {showAuthenticatedNav && isSiteAdmin && <MobileNavButton label="Site Admin" onClick={() => { window.location.hash = '#/site-admin'; }} />}

        {showAuthenticatedNav ? (
          <>
            <MobileNavButton label={getAuthUserDisplayName(user as any) || 'Profile'} onClick={() => navigate(`/users/${userId}`)} />
            {unreadMessages > 0 && (
              <Chip
                label={`Unread mail (${unreadMessages})`}
                color="warning"
                sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
              />
            )}
            <MobileNavButton label="Logout" onClick={handleLogout} variant="outlined" />
          </>
        ) : (
          <MobileNavButton label="Login / Sign Up" onClick={() => navigate('/login')} variant="contained" />
        )}
      </Drawer>
    </Box>
  );
}
